import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ConciergeBell, Eye, Info, KeyRound, RotateCcw, TriangleAlert } from 'lucide-react';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, Select } from '@/components/ui/field';
import { SwitchRow } from '@/components/ui/switch';
import { LoadingState, ErrorState, EmptyState } from '@/components/ui/states';
import { useRequireScope } from '../helpers';
import type { Branch } from '../types';
import {
  GUEST_SETTING_BOUNDS,
  changedSettings,
  hasChanges,
  useClearGuestSettingsMutation,
  useGuestSettingsQuery,
  useUpdateGuestSettingsMutation,
  type Bound,
  type GuestSettings,
  type TransparencySettings,
} from '../guestSettingsApi';
import ConfirmDialog from './ConfirmDialog';

/**
 * The venue's guest-facing switches, branch by branch.
 *
 * `/guest-settings` has been live since the transparency work and no screen has
 * ever pointed at it: every one of these twelve values could only be changed by
 * hand-writing a PATCH. That stopped being merely untidy when
 * `ordering.requireWaiterConfirmation` shipped defaulting to ON — it changed how
 * a guest's order reaches the kitchen in every venue, and there was no way in
 * any product to turn it back off.
 *
 * ## Why three sections and not one list of twelve
 *
 * The namespaces answer three different questions, and a manager scanning one
 * flat list would have to reconstruct which is which from the labels:
 *
 *  - **transparency** — what a guest is shown about the kitchen working on their
 *    food. Nothing here changes what the venue does, only what it says.
 *  - **session** — what a second phone has to prove before it joins a tab that
 *    is already open. This is about who can add to somebody's bill.
 *  - **ordering** — whether a guest's order stops for a waiter before the
 *    kitchen hears it. This one changes the operation, not the telling.
 *
 * ## Why the values on screen carry no "inherited" badge
 *
 * The GET answers the *effective* settings — the tenant's defaults with this
 * branch's overrides already folded over them — and never the raw JSON, so
 * there is nothing to read that would say which level a value came from. Rather
 * than invent a badge that would be a guess, the banner says plainly that these
 * are the values in force, and the clear action explains what dropping the
 * branch's own answers means. Structurally this panel is `WifiPanel`'s sibling:
 * same branch picker, same scope refusal, same "clear this branch's override".
 */

/**
 * The form the manager edits, with every number kept as the text they typed.
 *
 * Holding the numerics as `number` would mean the field either rejects
 * keystrokes or silently rewrites them mid-edit — clearing a `4` to type `12`
 * makes an empty box, which is not `0`. Text in, parsed at the boundary, and
 * `null` from the parse is what refuses the save.
 */
interface GuestSettingsForm {
  transparency: TransparencySettings;
  session: {
    requirePin: boolean;
    pinLength: string;
    maxPinAttempts: string;
    pinLockoutMinutes: string;
  };
  ordering: {
    requireWaiterConfirmation: boolean;
    confirmationSlaSeconds: string;
  };
}

type NumericField = keyof typeof GUEST_SETTING_BOUNDS;

function formOf(settings: GuestSettings): GuestSettingsForm {
  return {
    transparency: { ...settings.transparency },
    session: {
      requirePin: settings.session.requirePin,
      pinLength: String(settings.session.pinLength),
      maxPinAttempts: String(settings.session.maxPinAttempts),
      pinLockoutMinutes: String(settings.session.pinLockoutMinutes),
    },
    ordering: {
      requireWaiterConfirmation: settings.ordering.requireWaiterConfirmation,
      confirmationSlaSeconds: String(settings.ordering.confirmationSlaSeconds),
    },
  };
}

/**
 * The typed text as a whole number inside the server's bounds, or `null`.
 *
 * Digits only, on purpose: all four of these are `@IsInt()` with a positive
 * `@Min`, so `-4`, `4.5` and `4e3` are every bit as invalid as `900` and must
 * be refused here rather than by a toast that does not say what would work.
 * `pinLength` is the one that would not even fail loudly — the resolver *clamps*
 * it — so an out-of-range save would come back a different number than was
 * typed, with nothing on screen admitting it.
 */
function parseBounded(raw: string, bound: Bound): number | null {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return value >= bound.min && value <= bound.max ? value : null;
}

function numericValues(form: GuestSettingsForm): Record<NumericField, number | null> {
  return {
    pinLength: parseBounded(form.session.pinLength, GUEST_SETTING_BOUNDS.pinLength),
    maxPinAttempts: parseBounded(form.session.maxPinAttempts, GUEST_SETTING_BOUNDS.maxPinAttempts),
    pinLockoutMinutes: parseBounded(
      form.session.pinLockoutMinutes,
      GUEST_SETTING_BOUNDS.pinLockoutMinutes,
    ),
    confirmationSlaSeconds: parseBounded(
      form.ordering.confirmationSlaSeconds,
      GUEST_SETTING_BOUNDS.confirmationSlaSeconds,
    ),
  };
}

/** A number field that states its limits and refuses what the API would refuse. */
function BoundedField({
  label,
  description,
  bound,
  value,
  parsed,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  bound: Bound;
  value: string;
  /** `null` once the text stops being a number the API would accept. */
  parsed: number | null;
  disabled: boolean;
  onChange: (next: string) => void;
}) {
  const { t } = useTranslation();
  const range = t('settings.guest.range', { min: bound.min, max: bound.max });

  return (
    <Field
      label={label}
      hint={`${description} ${range}`}
      error={parsed === null ? t('settings.guest.outOfRange', { min: bound.min, max: bound.max }) : undefined}
    >
      <Input
        type="number"
        inputMode="numeric"
        min={bound.min}
        max={bound.max}
        step={1}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </Field>
  );
}

export default function GuestSettingsPanel() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const inScope = useRequireScope();
  const canView = can('settings.view');
  const canUpdate = can('settings.update');

  const branchesQuery = useApi<unknown>(canView ? '/branches' : null, { limit: 200 });
  const branches = rowsOf<Branch>(branchesQuery.data).rows;

  const [branchId, setBranchId] = useState('');
  const currentBranchId = branchId || branches[0]?.id || '';
  const branchName = branches.find((branch) => branch.id === currentBranchId)?.name ?? '';

  /**
   * Every `/guest-settings/branches/:branchId` route declares
   * `@RequireScope(BRANCH, 'branchId')` — the read included — so a branch-scoped
   * manager holding `settings.update` is still refused for someone else's
   * branch. The picker says which ones are theirs rather than letting the
   * selection turn into a 403, exactly as `WifiPanel` does.
   */
  const branchInScope = inScope('BRANCH', currentBranchId);
  const canManageBranch = canUpdate && branchInScope;

  const settingsQuery = useGuestSettingsQuery(currentBranchId, {
    skip: !currentBranchId || !canView,
  });
  const [saveSettings, saving] = useUpdateGuestSettingsMutation();
  const [clearSettings] = useClearGuestSettingsMutation();

  const baseline = settingsQuery.data;
  const [form, setForm] = useState<GuestSettingsForm | null>(null);
  const [clearing, setClearing] = useState(false);

  // The form is a draft of whatever the API last answered with, for whichever
  // branch is selected — including after a save or a clear, which is why this
  // follows the query rather than only the branch id.
  useEffect(() => setForm(baseline ? formOf(baseline) : null), [baseline]);

  const numbers = useMemo(() => (form ? numericValues(form) : null), [form]);

  /**
   * The draft as the API's own shape, or `null` while any number is unusable.
   * Nothing downstream has to re-check the bounds because there is no draft to
   * send until they hold.
   */
  const draft: GuestSettings | null =
    form && numbers &&
    numbers.pinLength !== null &&
    numbers.maxPinAttempts !== null &&
    numbers.pinLockoutMinutes !== null &&
    numbers.confirmationSlaSeconds !== null
      ? {
          transparency: form.transparency,
          session: {
            requirePin: form.session.requirePin,
            pinLength: numbers.pinLength,
            maxPinAttempts: numbers.maxPinAttempts,
            pinLockoutMinutes: numbers.pinLockoutMinutes,
          },
          ordering: {
            requireWaiterConfirmation: form.ordering.requireWaiterConfirmation,
            confirmationSlaSeconds: numbers.confirmationSlaSeconds,
          },
        }
      : null;

  const patch = baseline && draft ? changedSettings(baseline, draft) : {};
  const dirty = hasChanges(patch);
  const blocked = Boolean(form) && draft === null;

  if (!canView) return <EmptyState title={t('settings.noAccess')} hint={t('settings.noAccessHint')} />;
  if (branchesQuery.loading) return <LoadingState label={t('common.loading')} />;
  if (branchesQuery.error) {
    return <ErrorState message={branchesQuery.error} onRetry={branchesQuery.reload} />;
  }
  if (!branches.length) {
    return <EmptyState title={t('settings.branches.empty')} hint={t('settings.guest.needBranch')} />;
  }

  const editTransparency = (next: Partial<TransparencySettings>) =>
    setForm((current) =>
      current ? { ...current, transparency: { ...current.transparency, ...next } } : current,
    );
  const editSession = (next: Partial<GuestSettingsForm['session']>) =>
    setForm((current) =>
      current ? { ...current, session: { ...current.session, ...next } } : current,
    );
  const editOrdering = (next: Partial<GuestSettingsForm['ordering']>) =>
    setForm((current) =>
      current ? { ...current, ordering: { ...current.ordering, ...next } } : current,
    );

  const save = async () => {
    if (!dirty) return;
    try {
      await saveSettings({ branchId: currentBranchId, patch }).unwrap();
      toast.success(t('settings.guest.saved'));
    } catch (error) {
      toast.error(messageOf(error, t('settings.errors.saveFailed')));
    }
  };

  const clear = async () => {
    try {
      await clearSettings(currentBranchId).unwrap();
      toast.success(t('settings.guest.cleared'));
    } catch (error) {
      toast.error(messageOf(error, t('settings.errors.actionFailed')));
    }
  };

  return (
    <div className="space-y-5">
      <div className="max-w-xs">
        <Field label={t('settings.guest.branchLabel')}>
          <Select
            value={currentBranchId}
            onChange={(event) => setBranchId(event.target.value)}
            aria-label={t('settings.guest.branchLabel')}
          >
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
                {inScope('BRANCH', branch.id) ? '' : ` — ${t('settings.guest.branchOutOfScope')}`}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {/* The one thing about this screen that is not visible in the values. */}
      <div className="flex gap-3 rounded-lg bg-sky-50 p-4 text-sm text-sky-900 dark:bg-sky-950/50 dark:text-sky-200">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="space-y-1">
          <p className="font-medium">{t('settings.guest.effectiveTitle')}</p>
          <p>{t('settings.guest.effective')}</p>
        </div>
      </div>

      {settingsQuery.isLoading || !form ? (
        settingsQuery.error ? (
          <ErrorState
            message={messageOf(settingsQuery.error, t('settings.errors.loadFailed'))}
            onRetry={() => void settingsQuery.refetch()}
          />
        ) : (
          <LoadingState label={t('common.loading')} />
        )
      ) : (
        <>
          {canUpdate && !branchInScope && (
            <p className="text-sm text-amber-700 dark:text-amber-400">
              {t('settings.guest.branchOutOfScope')}
            </p>
          )}
          {!canUpdate && <p className="text-sm text-ink-muted">{t('settings.guest.readOnly')}</p>}

          <section className="space-y-2 rounded-xl border p-5">
            <SectionHeading
              icon={<Eye className="h-4 w-4" />}
              title={t('settings.guest.transparency.title')}
              hint={t('settings.guest.transparency.hint')}
            />

            <SwitchRow
              label={t('settings.guest.transparency.showEta')}
              hint={t('settings.guest.transparency.showEtaHint')}
              checked={form.transparency.showEta}
              disabled={!canManageBranch}
              onChange={(showEta) => editTransparency({ showEta })}
            />
            <SwitchRow
              label={t('settings.guest.transparency.showItemStatus')}
              hint={t('settings.guest.transparency.showItemStatusHint')}
              checked={form.transparency.showItemStatus}
              disabled={!canManageBranch}
              onChange={(showItemStatus) => editTransparency({ showItemStatus })}
            />
            <SwitchRow
              label={t('settings.guest.transparency.showStationName')}
              hint={t('settings.guest.transparency.showStationNameHint')}
              checked={form.transparency.showStationName}
              disabled={!canManageBranch}
              onChange={(showStationName) => editTransparency({ showStationName })}
            />
            <SwitchRow
              label={t('settings.guest.transparency.showDelayUpdates')}
              hint={t('settings.guest.transparency.showDelayUpdatesHint')}
              checked={form.transparency.showDelayUpdates}
              disabled={!canManageBranch}
              onChange={(showDelayUpdates) => editTransparency({ showDelayUpdates })}
            />

            {/* Off by default because the spec says so, and a pill in a list of
                five identical pills is exactly how that decision gets undone by
                someone flipping everything on to "be transparent". */}
            <div className="mt-2 rounded-lg border border-amber-300/70 bg-amber-50 p-4 dark:border-amber-900/70 dark:bg-amber-950/40">
              <div className="flex gap-3">
                <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" />
                <div className="min-w-0 space-y-1">
                  <p className="text-sm font-medium">
                    {t('settings.guest.transparency.queueTitle')}
                  </p>
                  <p className="text-xs text-ink-muted">
                    {t('settings.guest.transparency.queueWhy')}
                  </p>
                </div>
              </div>
              <SwitchRow
                label={t('settings.guest.transparency.showQueuePosition')}
                hint={t('settings.guest.transparency.showQueuePositionHint')}
                checked={form.transparency.showQueuePosition}
                disabled={!canManageBranch}
                onChange={(showQueuePosition) => editTransparency({ showQueuePosition })}
              />
            </div>
          </section>

          <section className="space-y-2 rounded-xl border p-5">
            <SectionHeading
              icon={<KeyRound className="h-4 w-4" />}
              title={t('settings.guest.session.title')}
              hint={t('settings.guest.session.hint')}
            />

            <SwitchRow
              label={t('settings.guest.session.requirePin')}
              hint={t('settings.guest.session.requirePinHint')}
              checked={form.session.requirePin}
              disabled={!canManageBranch}
              onChange={(requirePin) => editSession({ requirePin })}
            />

            {!form.session.requirePin && (
              <p className="text-xs text-ink-muted">{t('settings.guest.session.pinOffNote')}</p>
            )}

            <div className="grid gap-4 pt-2 sm:grid-cols-3">
              <BoundedField
                label={t('settings.guest.session.pinLength')}
                description={t('settings.guest.session.pinLengthHint')}
                bound={GUEST_SETTING_BOUNDS.pinLength}
                value={form.session.pinLength}
                parsed={numbers?.pinLength ?? null}
                disabled={!canManageBranch}
                onChange={(pinLength) => editSession({ pinLength })}
              />
              <BoundedField
                label={t('settings.guest.session.maxPinAttempts')}
                description={t('settings.guest.session.maxPinAttemptsHint')}
                bound={GUEST_SETTING_BOUNDS.maxPinAttempts}
                value={form.session.maxPinAttempts}
                parsed={numbers?.maxPinAttempts ?? null}
                disabled={!canManageBranch}
                onChange={(maxPinAttempts) => editSession({ maxPinAttempts })}
              />
              <BoundedField
                label={t('settings.guest.session.pinLockoutMinutes')}
                description={t('settings.guest.session.pinLockoutMinutesHint')}
                bound={GUEST_SETTING_BOUNDS.pinLockoutMinutes}
                value={form.session.pinLockoutMinutes}
                parsed={numbers?.pinLockoutMinutes ?? null}
                disabled={!canManageBranch}
                onChange={(pinLockoutMinutes) => editSession({ pinLockoutMinutes })}
              />
            </div>
          </section>

          <section className="space-y-2 rounded-xl border p-5">
            <SectionHeading
              icon={<ConciergeBell className="h-4 w-4" />}
              title={t('settings.guest.ordering.title')}
              hint={t('settings.guest.ordering.hint')}
            />

            <SwitchRow
              label={t('settings.guest.ordering.requireWaiterConfirmation')}
              hint={t('settings.guest.ordering.requireWaiterConfirmationHint')}
              checked={form.ordering.requireWaiterConfirmation}
              disabled={!canManageBranch}
              onChange={(requireWaiterConfirmation) => editOrdering({ requireWaiterConfirmation })}
            />
            <p className="text-xs text-ink-muted">{t('settings.guest.ordering.defaultNote')}</p>

            <div className="grid gap-4 pt-2 sm:grid-cols-3">
              <BoundedField
                label={t('settings.guest.ordering.confirmationSlaSeconds')}
                description={t('settings.guest.ordering.confirmationSlaSecondsHint')}
                bound={GUEST_SETTING_BOUNDS.confirmationSlaSeconds}
                value={form.ordering.confirmationSlaSeconds}
                parsed={numbers?.confirmationSlaSeconds ?? null}
                disabled={!canManageBranch || !form.ordering.requireWaiterConfirmation}
                onChange={(confirmationSlaSeconds) => editOrdering({ confirmationSlaSeconds })}
              />
            </div>
          </section>

          {canManageBranch && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                onClick={() => void save()}
                disabled={saving.isLoading || !dirty || blocked}
                title={blocked ? t('settings.guest.fixNumbers') : undefined}
              >
                {saving.isLoading ? t('settings.common.saving') : t('common.save')}
              </Button>
              <Button variant="outline" onClick={() => setClearing(true)} disabled={saving.isLoading}>
                <RotateCcw className="h-4 w-4" />
                {t('settings.guest.clear')}
              </Button>
              {blocked && (
                <span className="text-xs text-amber-700 dark:text-amber-400">
                  {t('settings.guest.fixNumbers')}
                </span>
              )}
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={clearing}
        title={t('settings.guest.clearTitle')}
        message={t('settings.guest.clearConfirm', { name: branchName })}
        confirmLabel={t('settings.guest.clear')}
        onConfirm={clear}
        onClose={() => setClearing(false)}
      />
    </div>
  );
}

function SectionHeading({
  icon,
  title,
  hint,
}: {
  icon: ReactNode;
  title: string;
  hint: string;
}) {
  return (
    <div className="pb-1">
      <h2 className="flex items-center gap-2 text-base font-semibold">
        {icon}
        {title}
      </h2>
      <p className="text-sm text-ink-muted">{hint}</p>
    </div>
  );
}

/**
 * `HttpExceptionFilter` answers `{ success:false, message }` where `message` is
 * a string or a `ValidationPipe` string[]; `axiosBaseQuery` has already
 * flattened both into `.message` by the time an RTK Query error reaches here, so
 * this only has to survive the shapes it did not flatten.
 */
function messageOf(error: unknown, fallback: string): string {
  const message = (error as { message?: unknown } | null)?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
}
